import { CoaStatus, CoaStatusLabel, VqcaStatus } from '@qcdoc/mock-runtime/contracts/enums.ts';
import { vendorApi, vendorService } from './vendorService.ts';
import { vendorPackagingService } from './vendorPackagingService.ts';
import type { PagedRequest } from '../../types/common/common.ts';

export interface VendorDashboardStats { myVqcas: number; approvedVqcas: number; pendingVqcas: number; myCoas: number; myPackages: number }
export interface VendorDashboardTrendItem { month: string; coas: number; vqcas: number; packages: number }
export interface VendorDashboardCoaBreakdown { name: string; value: number }
export interface VendorDashboardData { stats: VendorDashboardStats; trend: VendorDashboardTrendItem[]; coaBreakdown: VendorDashboardCoaBreakdown[] }

export function vendorDashboardMonths(now = new Date()) {
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      label: date.toLocaleString('en-US', { month: 'short' }), year: date.getFullYear(), month: date.getMonth(),
      start: date.toISOString(), end: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).toISOString(),
    };
  });
}

const coaCount = async (vendorId: string, filters: Record<string, unknown>) =>
  (await vendorApi.getCoaList(vendorId, { page: 1, pageSize: 1, filters } as PagedRequest)).data.totalCount;

export const vendorDashboardService = {
  async getDashboard(vendorId: string): Promise<VendorDashboardData> {
    const months = vendorDashboardMonths();
    const [vqcaRows, pendingVqcas, packages, myCoas, passed, underReview, conditional, failed, created, monthlyCoas] = await Promise.all([
      vendorService.getVqcaList(vendorId),
      vendorService.getVqcaPending(vendorId),
      vendorPackagingService.search({ page: 1, pageSize: 1000, sortField: 'createdDate', sortDirection: 'desc' }),
      coaCount(vendorId, {}),
      coaCount(vendorId, { statuses: String(CoaStatus.Passed) }),
      coaCount(vendorId, { statuses: String(CoaStatus.UnderReview) }),
      coaCount(vendorId, { statuses: String(CoaStatus.ConditionallyApproved) }),
      coaCount(vendorId, { statuses: String(CoaStatus.Failed) }),
      coaCount(vendorId, { statuses: String(CoaStatus.Created) }),
      Promise.all(months.map((month) => coaCount(vendorId, { issueDateRange: [month.start, month.end] }))),
    ]);
    const approvedVqcas = vqcaRows.filter((row) => row.statusId === VqcaStatus.Approved).length;
    return {
      stats: { myVqcas: vqcaRows.length, approvedVqcas, pendingVqcas: pendingVqcas.length, myCoas, myPackages: packages.totalCount },
      trend: months.map((month, index) => ({
        month: month.label,
        coas: monthlyCoas[index],
        vqcas: vqcaRows.filter((row) => {
          const date = new Date(row.signedDate ?? row.issueDate ?? '');
          return date.getFullYear() === month.year && date.getMonth() === month.month;
        }).length,
        packages: packages.items.filter((row) => {
          const date = new Date(row.createdDate ?? '');
          return date.getFullYear() === month.year && date.getMonth() === month.month;
        }).length,
      })),
      coaBreakdown: [
        { name: CoaStatusLabel[CoaStatus.Passed], value: passed },
        { name: CoaStatusLabel[CoaStatus.UnderReview], value: underReview },
        { name: CoaStatusLabel[CoaStatus.ConditionallyApproved], value: conditional },
        { name: CoaStatusLabel[CoaStatus.Failed], value: failed },
        { name: CoaStatusLabel[CoaStatus.Created], value: created },
      ],
    };
  },
};
